import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { AlertTriangle } from "lucide-react";

// Guard anti-duplicazione centralizzato per la creazione di anagrafiche/compagnie.
// Uso: import { guardCreate } from ".../context/DuplicateGuard"
//   const { action, record } = await guardCreate(atype, payload)
//   action: "created" | "existing"
// Sotto: POST /anagrafiche/{atype}?check_duplicate=1; se il backend risponde 409 duplicato,
// mostra un dialog con "Usa/Apri esistente" oppure "Crea nuova" (re-POST con allow_duplicate).
let _impl = null;

export function guardCreate(atype, payload) {
  if (_impl) return _impl(atype, payload);
  return api.post(`/anagrafiche/${atype}`, payload).then((r) => ({ action: "created", record: r.data }));
}

const isoToGG = (s) => {
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s);
};

function buildMessage(reason, match) {
  const disp = match ? (match.denominazione || [match.nome, match.cognome].filter(Boolean).join(" ") || "—") : "—";
  const dob = isoToGG(match?.data_nascita);
  switch (reason) {
    case "compagnia":
      return { text: `Attenzione: esiste già la compagnia "${disp}". Vuoi usare quella esistente o crearne una nuova?`, existing: "Usa esistente", create: "Crea nuova" };
    case "cf":
      return { text: `Attenzione: esiste già un soggetto con questo Codice Fiscale: ${disp}. Vuoi aprire la scheda esistente o crearne una nuova?`, existing: "Apri esistente", create: "Crea nuova" };
    case "piva":
      return { text: `Attenzione: esiste già un soggetto con questa Partita IVA: ${disp}. Vuoi aprire la scheda esistente o crearne una nuova?`, existing: "Apri esistente", create: "Crea nuova" };
    case "denominazione":
      return { text: `Attenzione: esiste già un'anagrafica con questa denominazione: "${disp}". Vuoi aprire quella esistente o crearne una nuova?`, existing: "Apri esistente", create: "Crea nuova" };
    case "name_dob":
      return { text: `Attenzione: esiste già un soggetto con questo nome e data di nascita: ${disp} - ${dob}. Continuare?`, existing: "Apri esistente", create: "Crea nuovo" };
    case "name":
    default:
      return { text: `Attenzione: esiste già un soggetto con questo nome: ${disp}. Continuare?`, existing: "Apri esistente", create: "Crea nuovo" };
  }
}

export function DuplicateGuardProvider({ children }) {
  const [state, setState] = useState(null); // { reason, match }
  const resolver = useRef(null);

  const impl = useCallback((atype, payload) => new Promise((resolve, reject) => {
    (async () => {
      try {
        const { data } = await api.post(`/anagrafiche/${atype}?check_duplicate=1`, payload);
        resolve({ action: "created", record: data });
      } catch (e) {
        const det = e.response?.data?.detail;
        if (e.response?.status === 409 && det && det.code === "duplicate") {
          resolver.current = async (choice) => {
            resolver.current = null;
            setState(null);
            if (choice === "existing") { resolve({ action: "existing", record: det.match }); return; }
            try {
              const { data } = await api.post(`/anagrafiche/${atype}?check_duplicate=1`, { ...payload, allow_duplicate: true });
              resolve({ action: "created", record: data });
            } catch (e2) { reject(e2); }
          };
          setState({ reason: det.reason, match: det.match });
        } else { reject(e); }
      }
    })();
  }), []);

  useEffect(() => { _impl = impl; return () => { if (_impl === impl) _impl = null; }; }, [impl]);

  const decide = (choice) => { const r = resolver.current; if (r) r(choice); };
  const msg = state ? buildMessage(state.reason, state.match) : null;

  return (
    <>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => { if (!o) decide("new"); }}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="dup-guard-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" /> Possibile duplicato
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-200 leading-relaxed" data-testid="dup-guard-message">{msg?.text}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => decide("existing")} data-testid="dup-guard-existing"
              className="border-slate-600 text-slate-100 hover:bg-slate-800">{msg?.existing}</Button>
            <Button onClick={() => decide("new")} data-testid="dup-guard-create"
              className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{msg?.create}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
